import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { nanoid } from "nanoid";

interface UploadOptions {
  bucket: string;
  folder?: string;
  onProgress?: (progress: number) => void;
}

/**
 * Centralized file upload hook with progress tracking and error handling
 * Replaces duplicated upload logic across multiple components
 */
export const useFileUpload = (orgId: string) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadFile = async (
    file: File,
    options: UploadOptions
  ): Promise<{ url: string; path: string } | null> => {
    if (!orgId) {
      toast.error("Organization not found");
      return null;
    }

    setUploading(true);
    setProgress(0);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${nanoid()}.${fileExt}`;
      const folder = options.folder || 'uploads';
      const filePath = `${orgId}/${folder}/${fileName}`;

      const { error: uploadError, data } = await supabase.storage
        .from(options.bucket)
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(options.bucket)
        .getPublicUrl(filePath);

      setProgress(100);
      options.onProgress?.(100);
      
      return {
        url: publicUrl,
        path: filePath,
      };
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(`Upload failed: ${error.message}`);
      return null;
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const uploadMultiple = async (
    files: File[],
    options: UploadOptions
  ): Promise<Array<{ url: string; path: string }>> => {
    const results = [];
    for (let i = 0; i < files.length; i++) {
      const result = await uploadFile(files[i], options);
      if (result) {
        results.push(result);
      }
      options.onProgress?.((i + 1) / files.length * 100);
    }
    return results;
  };

  return {
    uploadFile,
    uploadMultiple,
    uploading,
    progress,
  };
};
